import { useCallback, useContext, useMemo, useState } from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";

import { TextPrimary, TextSecondary, TicketCard } from "theme";

import { emitEvent, useChain, useDappSDKProvider } from "dapp-sdk-v2";
import { EVENTS, TRANSACTION_TYPE } from "dapp-sdk-v2/constants/events";

import UndelegationMessage from "@components/UndelegationMessage";

import { formatNumber } from "@utils/globalCommon";

import { PageContext } from "@providers/PageProvider";

import usePageData from "@hooks/usePageData";

import ClaimTypes from "./Components/ClaimTypes/ClaimTypes";
import { RADIO_BUTTONS } from "./constants";
import { TxType } from "./types";
import { getWithdrawalData } from "./helpers";
import { HEDERA, SD } from "@constants/constants";
import { AiOutlineInfoCircle } from "react-icons/ai";

const TOKEN = process.env.NEXT_PUBLIC_TOKEN;

interface ClaimProps {
  onUnstakeClick: () => void;
}

const Claim = ({ onUnstakeClick }: ClaimProps) => {
  const {
    isNativeChain,
    switchNetwork,
    withdrawals = [],
  } = useDappSDKProvider();
  const [withdrawType, setWithDrawType] = useState(RADIO_BUTTONS[0].value);
  const { status, switchChain } = useChain();
  const data = useContext(PageContext);

  const { isSafeMode } = data;

  const {
    undelegation_warning_message,
    switch_network_cta = "Switch Network",
    withdraw_cta = "Withdraw",
    erc20_token,
    no_withdraw_requests_title = "No requests found",
    no_withdraw_requests_description,
    no_withdraw_requests_description_cta,
    days_to_wait_for_withdraw = 15,
    is_withdraw_tabs_options = false,
    undelegation_warning_message_safe_mode,
  } = usePageData("$.page.data")[0] || {};

  const claimedTransactions = useMemo(() => {
    return withdrawals.filter((t: TxType) => t?.isWithDrawn).reverse();
  }, [withdrawals]);

  const unClaimedTransactions = useMemo(() => {
    return withdrawals.filter((t: TxType) => !t?.isWithDrawn);
  }, [withdrawals]);

  const onRadioValueChange = (value: string) => {
    setWithDrawType(value);
  };

  const withdrawData = useMemo(() => {
    return is_withdraw_tabs_options
      ? withdrawType === "active"
        ? unClaimedTransactions
        : claimedTransactions
      : withdrawals;
  }, [
    claimedTransactions,
    is_withdraw_tabs_options,
    unClaimedTransactions,
    withdrawType,
    withdrawals,
  ]);

  const getWithdrawActionField = useCallback(
    (
      isClaimable: boolean,
      message: string,
      amount: string,
      index: string,
      position: number | undefined
    ) => {
      if (isSafeMode) {
        return "NA";
      } else if (isClaimable) {
        return (
          <Button
            fontWeight="700"
            fontSize={{ base: "10px", md: "12px" }}
            bg="primary"
            color="white"
            _dark={{
              bg: "white",
              color: "#142528",
            }}
            p="4px 12px"
            h="2rem"
            onClick={() => handleWithdraw(+index, amount)}
          >
            {withdraw_cta}
          </Button>
        );
      } else if (TOKEN === SD && position) {
        return (
          <Flex gap="8px" alignItems="center" justifyContent="flex-end">
            <Text>{`Withdrawal queue position: ${position}`}</Text>
            <Tooltip
              placement="top"
              p="16px"
              borderRadius="8px"
              maxW="290px"
              label={
                <Box pointerEvents="all">
                  <Text color="textSecondary" fontWeight="400" fontSize="12px">
                    This number indicates your current position in the
                    withdrawal queue. Once the withdrawal is processed, the
                    claim button will be enabled, allowing you to claim your SD
                    by clicking on it.
                  </Text>
                </Box>
              }
              closeDelay={2000}
              bg="tooltipBackground"
              alignItems="center"
            >
              <Text ml="4px" color="textSecondary" fontSize="14px">
                <AiOutlineInfoCircle />
              </Text>
            </Tooltip>
          </Flex>
        );
      }
      return message;
    },
    [isSafeMode, withdraw_cta]
  );

  const pageData = useMemo(() => {
    return (
      <>
        {withdrawData?.map((withdrawal: TxType, indexx: number) => {
          const { updatedDate, index, isClaimable, amount, position } =
            getWithdrawalData(
              withdrawal,
              days_to_wait_for_withdraw,
              isNativeChain
            );

          return (
            <Tr key={indexx} fontWeight="400" color="textSecondary">
              <Td fontSize="14px" color="textPrimary" fontWeight="600">
                {formatNumber(Number(amount), false, 4, true)}{" "}
                <Text fontWeight="400" as="span">
                  {erc20_token}
                </Text>
              </Td>
              {TOKEN === HEDERA && (
                <Td
                  fontSize="14px"
                  color="textPrimary"
                  fontWeight="400"
                  textAlign="center"
                >
                  {formatNumber(
                    Number(withdrawal.exchangeRate),
                    false,
                    4,
                    true
                  )}
                </Td>
              )}
              <Td
                fontSize={{ base: "10px", md: "12px" }}
                textAlign="right"
                whiteSpace="pre-line"
              >
                {getWithdrawActionField(
                  isClaimable,
                  updatedDate,
                  amount,
                  index,
                  position
                )}
              </Td>
            </Tr>
          );
        })}
      </>
    );
  }, [
    days_to_wait_for_withdraw,
    erc20_token,
    getWithdrawActionField,
    isNativeChain,
    withdrawData,
  ]);

  function handleWithdraw(index: number, amount: string) {
    emitEvent(EVENTS.HANDLE_TRANSACTION, {
      type: TRANSACTION_TYPE.WITHDRAW,
      id: index,
      amount,
    });
  }

  const tableRows = useMemo(() => {
    return (
      <Tr fontWeight="700" fontSize="12px">
        <Th textTransform="none">Amount</Th>
        {TOKEN === HEDERA && (
          <Th textTransform="none" textAlign="center">
            Exchange rate
          </Th>
        )}
        <Th textTransform="none" textAlign="right">
          {withdrawType === "active" ? "Estimated time" : "Withdrew on"}
        </Th>
      </Tr>
    );
  }, [withdrawType]);

  return (
    <TicketCard
      p={{
        base: "1.5rem 1rem 1rem",
        xl: "2rem",
      }}
      minH={{ base: "371px", md: "395px" }}
    >
      {is_withdraw_tabs_options && claimedTransactions?.length > 0 ? (
        <ClaimTypes
          radioButtons={RADIO_BUTTONS}
          onRadioChange={onRadioValueChange}
        />
      ) : null}

      {withdrawData && withdrawData?.length > 0 ? (
        withdrawType === "active" && !status && switchNetwork ? (
          <Center
            flexDirection="column"
            p={{
              base: "3rem 1rem 1rem",
              xl: "5rem 2rem 2.5rem",
            }}
            textAlign="center"
          >
            <Text
              color="textPrimary"
              fontWeight="600"
              fontSize={{
                base: "20px",
                xl: "24px",
              }}
              textAlign="center"
            >
              {no_withdraw_requests_title}
            </Text>

            <Button
              color="white"
              height="56px"
              mt="4rem"
              w="100%"
              fontWeight="600"
              fontSize="16px"
              borderRadius="12px"
              onClick={switchChain}
            >
              {switch_network_cta}
            </Button>
          </Center>
        ) : (
          <Flex flexDirection="column">
            {undelegation_warning_message ||
            (isSafeMode && undelegation_warning_message_safe_mode) ? (
              <Box mb={{ base: "1.5rem", md: "2rem" }}>
                <UndelegationMessage
                  message={
                    isSafeMode
                      ? undelegation_warning_message_safe_mode
                      : TOKEN === HEDERA
                      ? `${no_withdraw_requests_description}${no_withdraw_requests_description_cta}`
                      : undelegation_warning_message
                  }
                />
              </Box>
            ) : null}

            <Box>
              <TableContainer
                overflowY="auto"
                mt={is_withdraw_tabs_options ? "2rem" : 0}
                height={
                  is_withdraw_tabs_options
                    ? { base: "250px", xl: "234px" }
                    : "320px"
                }
              >
                <Table variant="simple" p="0">
                  <Thead>{tableRows}</Thead>

                  <Tbody>{pageData}</Tbody>
                </Table>
              </TableContainer>
            </Box>
          </Flex>
        )
      ) : (
        <Center
          flexDirection="column"
          p={{
            base: "3rem 1rem 1rem",
            xl: "5rem 2rem 2.5rem",
          }}
          textAlign="center"
        >
          <TextPrimary
            fontWeight="600"
            fontSize={{
              base: "20px",
              xl: "24px",
            }}
            textAlign="center"
          >
            {no_withdraw_requests_title}
          </TextPrimary>

          {no_withdraw_requests_description && (
            <TextSecondary
              mt="2rem"
              fontWeight="400"
              fontSize={{
                base: "12px",
                xl: "14px",
              }}
            >
              {no_withdraw_requests_description}
              {no_withdraw_requests_description_cta ? (
                <Link color="primary" onClick={onUnstakeClick}>
                  {no_withdraw_requests_description_cta}
                </Link>
              ) : null}
            </TextSecondary>
          )}
        </Center>
      )}
    </TicketCard>
  );
};

export default Claim;
